import { useMutation, useQuery } from "@tanstack/react-query";
import { apiPost, apiGet, apiPut } from "@/lib/apiClient";
import type {
  CreateImportantDateRequest,
  CreateUserRequest,
  DeleteImportantDateRequest,
  EditImportantDateRequest,
  ImportantDate,
  ImportantDateCategory,
  RoleResponse,
  UpdateUserRequest,
  UserResponse,
  ProfileSearchResponse
} from "@/types/settings";

export const settingsKeys = {
  importantDates: () => ["settings", "important-dates"] as const,
  importantDateCategories: () => ["settings", "important-date-categories"] as const,
  users: () => ["settings", "users"] as const,
  userByEmail: (email: string) => ["settings", "user", email] as const,
  userRoles: () => ["settings", "user-roles"] as const,
  businesses: () => ["settings", "businesses"] as const,
  meetingTypes: () => ["settings", "meeting-types"] as const,
  profileSearch: (searchText: string) => ["settings", "profile-search", searchText] as const
};

export function useImportantDates() {
  return useQuery({
    queryKey: settingsKeys.importantDates(),
    queryFn: () => apiGet<ImportantDate[]>("ImportantDates/GetAllImportantDates"),
    staleTime: 30_000,
    refetchOnMount: "always"
  });
}

export function useImportantDateCategories() {
  return useQuery({
    queryKey: settingsKeys.importantDateCategories(),
    queryFn: () => apiGet<ImportantDateCategory[]>("ImportantDates/GetAllImportantDateCategories"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useCreateImportantDate() {
  return useMutation({
    mutationFn: (body: CreateImportantDateRequest) =>
      apiPost("ImportantDates/CreateImportantDate", body)
       
  });
}

export function useEditImportantDate() {
  return useMutation({
    mutationFn: (body: EditImportantDateRequest) =>
      apiPost("ImportantDates/EditImportantDate", body)
      
  });
}

export function useDeleteImportantDate() {
  return useMutation({
    mutationFn: (body: DeleteImportantDateRequest) =>
      apiPost("ImportantDates/DeleteImportantDate", body)
  });
}

export function useUsers() {
  return useQuery({
    queryKey: settingsKeys.users(),
    queryFn: () => apiGet<UserResponse[]>("user/GetAllUsers"),
    staleTime: 30_000,
    refetchOnMount: "always"
  });
}

export function useUserByEmail(email: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: settingsKeys.userByEmail(email),
    queryFn: () => apiGet<UserResponse>(`user/${encodeURIComponent(email)}`),
    enabled: options?.enabled !== undefined ? options.enabled : Boolean(email),
    // Fetch once per browser session — all useCurrentUser() / usePermissions() calls
    // read from this cache entry without triggering additional API requests.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

export function useUserRoles() {
  return useQuery({
    queryKey: settingsKeys.userRoles(),
    queryFn: () => apiGet<RoleResponse[]>("user/GetAllUserRoles"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useBusinesses() {
  return useQuery({
    queryKey: settingsKeys.businesses(),
    queryFn: () => apiGet<string[]>("Business/GetBusinesses"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useMeetingTypes() {
  return useQuery({
    queryKey: settingsKeys.meetingTypes(),
    queryFn: () => apiGet<string[]>("MeetingType/GetMeetingTypes"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useProfileSearch(searchText: string) {
  const normalized = searchText.trim();
  return useQuery({
    queryKey: settingsKeys.profileSearch(normalized),
    queryFn: () => {
      const qs = new URLSearchParams({
        searchtext: normalized,
      });
      return apiGet<ProfileSearchResponse[]>(`ProfileSearch/searchDeloitteProfile?${qs}`);
    },
    enabled: normalized.length >= 2,
    staleTime: 30_000
  });
}

export function useCreateUser() {
  return useMutation({
    mutationFn: (body: CreateUserRequest) =>
      apiPost("user/CreateUser", body)
  });
}

export function useUpdateUser() {
  return useMutation({
    mutationFn: (body: UpdateUserRequest) =>
      apiPut<UpdateUserRequest, boolean>(`user/${encodeURIComponent(body.email)}`, body)
  });
}

export function useDeleteUser() {
  return useMutation({
    mutationFn: (email: string) =>
      apiPost(`User/Delete/${encodeURIComponent(email)}`, {})
  });
}
